import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Search, ScanLine, X, Loader2, Ban, CheckCircle2, HelpCircle } from "lucide-react";

const NO_ID_REASONS = [
  { value: "discontinued", label: "Discontinued — no longer made" },
  { value: "custom_build", label: "Custom or one-off build" },
  { value: "used_no_box", label: "Used, no box or markings" },
];

const inputCls =
  "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors font-body placeholder:text-[#3E3F42]";
const labelCls = "font-mono text-xs uppercase tracking-wide text-secondary-foreground block mb-1";

// Identifier-first step: the FIRST thing a seller does. Resolve a UPC/SKU (or type-ahead by name)
// against the catalog, or declare there's no identifier with a reason. The parent decides what to do
// with the result (prefill / refuse / manual-candidate) — this component only captures intent.
export const CatalogIdentifier = ({ onResolve, onNoId, onClear, resolvedSummary, blockedInfo, notFoundInfo, showNoId = true, wtb = false }) => {
  const [ident, setIdent] = useState("");
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [showMatches, setShowMatches] = useState(false);
  const [noId, setNoId] = useState(false);
  const [reason, setReason] = useState("");
  const [scanErr, setScanErr] = useState("");
  const canScan = typeof window !== "undefined" && "BarcodeDetector" in window;
  const debTimer = useRef(null);

  // Type-ahead search (debounced) — for the seller who doesn't have the box in hand.
  useEffect(() => {
    if (noId) return;
    const q = query.trim();
    if (q.length < 2) { setMatches([]); return; }
    clearTimeout(debTimer.current);
    debTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/catalog/search", { params: { q } });
        setMatches(data.results || []);
        setShowMatches(true);
      } catch { setMatches([]); }
    }, 250);
    return () => clearTimeout(debTimer.current);
  }, [query, noId]);

  const doResolve = async (identifier) => {
    const id = (identifier || "").trim();
    if (!id) return;
    setBusy(true); setShowMatches(false);
    try {
      const { data } = await api.get("/catalog/resolve", { params: { identifier: id } });
      if (data && data.result === "ambiguous") {
        // Model-name "Look up" hit several products — show the matches so the seller picks the exact
        // one (instead of a silent "no match"). No upstream banner while they choose.
        onClear?.();
        setMatches(data.matches || []);
        setShowMatches(true);
        return;
      }
      onResolve?.({ ...data, identifier: id });
    } catch {
      onResolve?.({ result: "unresolved", identifier: id });
    } finally { setBusy(false); }
  };

  const pickMatch = (m) => {
    setIdent(m.upc || m.sku || "");
    setQuery(`${m.brand} ${m.model}`.trim());
    setShowMatches(false);
    doResolve(m.upc || m.sku);
  };

  const toggleNoId = (checked) => {
    setNoId(checked);
    if (checked) { setIdent(""); setQuery(""); setMatches([]); onClear?.(); }
    onNoId?.({ checked, reason: checked ? reason : "" });
  };

  const changeReason = (r) => { setReason(r); onNoId?.({ checked: true, reason: r }); };

  const scan = async () => {
    setScanErr("");
    try {
      const detector = new window.BarcodeDetector({ formats: ["upc_a", "upc_e", "ean_13", "ean_8", "code_128"] });
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      const video = document.createElement("video");
      video.srcObject = stream; await video.play();
      let found = null;
      for (let i = 0; i < 40 && !found; i++) {
        const codes = await detector.detect(video);
        if (codes && codes.length) found = codes[0].rawValue;
        await new Promise((r) => setTimeout(r, 150));
      }
      stream.getTracks().forEach((t) => t.stop());
      if (found) { setIdent(found); doResolve(found); }
      else setScanErr("No barcode detected — enter it by hand.");
    } catch {
      setScanErr("Couldn't open the camera — enter the code by hand.");
    }
  };

  return (
    <div className="border border-olive/40 bg-olive/5 p-4 space-y-3" data-testid="catalog-identifier">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-heading uppercase text-sm tracking-tight text-foreground">{wtb ? "Name the exact product" : "Start with the barcode"}</span>
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{wtb ? "Optional — helps sellers & saved searches find you" : "Step 1 — we fill in the rest"}</span>
      </div>

      {!noId && (
        <>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={ident || query}
                inputMode="text"
                onChange={(e) => {
                  const v = e.target.value;
                  setIdent(v); setQuery(v);
                  if (!v) { onClear?.(); }
                }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doResolve(ident || query); } }}
                onFocus={() => matches.length && setShowMatches(true)}
                placeholder='e.g. 843829143210  or  "razor gen 3 6-36"'
                className={`${inputCls} pl-9`}
                data-testid="catalog-identifier-input"
              />
              {showMatches && matches.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 border border-sniper bg-surface max-h-64 overflow-y-auto" data-testid="catalog-matches">
                  {matches.map((m) => (
                    <button
                      type="button"
                      key={m.catalog_id}
                      onClick={() => pickMatch(m)}
                      data-testid={`catalog-match-${m.catalog_id}`}
                      className="w-full text-left px-3 py-2 hover:bg-olive/10 border-b border-sniper/50 last:border-0 flex items-center justify-between gap-2"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground truncate">{m.brand} {m.model}</span>
                        <span className="block font-mono text-[10px] text-muted-foreground truncate">{m.source_title}</span>
                      </span>
                      {!m.listable && <Ban className="w-3.5 h-3.5 text-blaze shrink-0" title="Not listable" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" onClick={() => doResolve(ident || query)} disabled={busy}
              data-testid="catalog-lookup-btn"
              className="border border-olive hover:bg-olive/10 text-olive-bright font-mono uppercase text-xs px-4 transition-colors disabled:opacity-50 flex items-center gap-1.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Look up"}
            </button>
            {canScan && (
              <button type="button" onClick={scan} data-testid="catalog-scan-btn" title="Scan a barcode"
                className="border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground px-3 transition-colors">
                <ScanLine className="w-4 h-4" />
              </button>
            )}
          </div>
          {scanErr && <p className="font-mono text-[11px] text-blaze" data-testid="catalog-scan-error">{scanErr}</p>}
        </>
      )}

      {/* resolved / blocked feedback */}
      {resolvedSummary && (
        <div className="flex items-start gap-2 border border-olive/40 bg-olive/10 px-3 py-2" data-testid="catalog-resolved">
          <CheckCircle2 className="w-4 h-4 text-olive-bright shrink-0 mt-0.5" />
          <p className="text-sm text-secondary-foreground font-body">{resolvedSummary}</p>
        </div>
      )}
      {blockedInfo && (
        <div className="flex items-start gap-2 border border-blaze/50 bg-blaze/10 px-3 py-2" data-testid="catalog-blocked">
          <Ban className="w-4 h-4 text-blaze shrink-0 mt-0.5" />
          <p className="text-sm text-blaze font-body">{blockedInfo}</p>
        </div>
      )}
      {notFoundInfo && !resolvedSummary && !blockedInfo && (
        <div className="flex items-start gap-2 border border-sniper bg-surface px-3 py-2" data-testid="catalog-notfound">
          <HelpCircle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-sm text-secondary-foreground font-body">{notFoundInfo}</p>
        </div>
      )}

      {/* no-identifier path (Sell only — a Want-to-Buy never requires an identifier or a reason) */}
      {showNoId && (
        <>
          <label className="flex items-center gap-2 cursor-pointer pt-1" data-testid="catalog-noid-row">
            <input type="checkbox" checked={noId} onChange={(e) => toggleNoId(e.target.checked)} className="accent-olive w-4 h-4" data-testid="catalog-noid-check" />
            <span className="font-mono text-xs uppercase text-secondary-foreground">No UPC or SKU available</span>
            <HelpCircle className="w-3.5 h-3.5 text-muted-foreground" title="Discontinued, custom, or a used item with no box/markings" />
          </label>
          {noId && (
            <div>
              <label className={labelCls}>Why is there no identifier? (required)</label>
              <select value={reason} onChange={(e) => changeReason(e.target.value)} className={inputCls} data-testid="catalog-noid-reason">
                <option value="">Choose a reason…</option>
                {NO_ID_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          )}
        </>
      )}
    </div>
  );
};
